/**
 * The Mock-ups page's catalogue: the templates on THIS device, UNIONED with the cloud
 * listing, and the rules for when a local row may be deleted.
 *
 * ── what was wrong ─────────────────────────────────────────────────────────────────────
 * The Mock-up Generator rendered from `listSyncedTemplates(io)` alone — the device-local
 * `synced_templates` store — and nothing reconciled that store against the server. Its only
 * writers were the Templates page's Sync/Force sync, the auto-sync ticker's re-hydrate (which
 * updates rows it ALREADY has) and Unsync. So a template created in the cloud never appeared,
 * a template deleted in the cloud never left, and the page's own Refresh re-read the same
 * unchanged store.
 *
 * ── what this does ─────────────────────────────────────────────────────────────────────
 * ONE list, from two sources:
 *   · device records — full, with geometry, selectable instantly and offline;
 *   · cloud summaries — everything else the account can see, shown with a "Not synced here"
 *     marker and costing NOTHING to display. The full record is fetched the moment the
 *     operator actually selects one (`syncOneTemplate`), never on their behalf.
 * A local row the listing does not return is EVICTED (record + every view's bake + case image
 * + hydration bookkeeping), which is the cross-device rule in `docs/release-audit-checklist.md`
 * applied to a delete: the cloud record is the only copy and the local one is a cache.
 *
 * ── the eviction authority, stated once ────────────────────────────────────────────────
 * Deleting a multi-MB local cache on the strength of a list is only safe when the list is
 * PROVABLY complete. Every rule below exists because one way of getting that wrong destroys
 * data the operator has to re-download:
 *
 *  1. PAGE IT. `GET /api/templates` with no `limit` returns a BARE ARRAY capped at 500
 *     (`cloud/app/catalog/paging.py` `LEGACY_CAP`) and carries no "there is more" signal, so
 *     an account with 501 templates would have had the other 1 — and at 2,000, the other
 *     1,500 — evicted on sight. The listing is read through `listTemplatesPage` until
 *     `nextCursor` is null, and a failure on ANY page aborts the whole reconcile.
 *  2. AN EMPTY LIST IS SUSPECT, NOT AUTHORITATIVE. `jsonClient.request` turns an unparseable
 *     200 body into `undefined`, which `templateRows` turns into `[]`. That is
 *     indistinguishable from "this account has no templates" and would wipe the store. When
 *     the cloud says nothing and the device holds something, nothing is removed and the page
 *     says so.
 *  3. ONLY EVICT WHAT THE LIST CAN REPRESENT. `/api/templates` filters `is_library == False`
 *     and scopes to own-tenant + shared (`router.py`), while `/api/templates/sync` also
 *     returns library rows and rows an admin GRANTED to this tenant. A library/platform row is
 *     therefore absent from the browse listing by construction, not by deletion, so it is
 *     judged against `listSyncable()` when the client has it and LEFT ALONE when it does not.
 *  4. GRACE FOR A ROW WRITTEN SECONDS AGO. A listing fetched before a Sync finished cannot
 *     contain what that Sync downloaded. A row this device wrote inside `RECENT_SAVE_GRACE_MS`
 *     is never evicted.
 *  5. ABSENCE IS PROVED BY SUCCESS ONLY. Any transport failure means offline: the cached rows
 *     stay, nothing is deleted, and the caller says so on screen.
 *
 * ── the status gate ────────────────────────────────────────────────────────────────────
 * D-4 (iii) is a PERMISSION rule, not a page's taste: an admin sees every status, a customer
 * sees Active only. `rowSet.visibleRows` applies it on the Templates page and this applies the
 * same one here, so the two surfaces cannot disagree about what a customer may use.
 *
 * NOTE for whoever next opens `rowSet.ts`: its `rowSetCounts` docstring still says a customer's
 * gated Draft "still sees it in the Mockup Generator, whose source is the LOCAL store". That
 * stopped being true here. The file is pinned byte-for-byte by the design gate's G-31, so the
 * correction is recorded in this comment and in `docs/frontend-flow.md` rather than made there.
 *
 * ── wording ────────────────────────────────────────────────────────────────────────────
 * A row that left the listing may have been deleted, un-shared, or put out of reach by a role
 * change. The page cannot tell those apart and does not pretend to: it says the template is
 * "no longer available to this account".
 */
import type { ProductTemplate, TemplatePage, PlatformIO } from "@genz/platform";
import {
  listSyncedTemplateRows, removeSyncedTemplate, saveSyncedTemplate,
  type SyncedTemplateRow,
} from "./localTemplates.js";
import { facetMatches, headStatusOf } from "./templateLabels.js";
import { clearHydration } from "../stores/hydrationStore.js";
import type { deleteTemplateImage } from "../stores/imageStore.js";

/** Just the storage slice these routines need — keeps them trivially unit-testable. */
type ReconcileIO = Pick<PlatformIO, "localStore">;

/** Rows per request. Well under the server's 500 cap, so one slow page never blocks long. */
export const PAGE_LIMIT = 200;
/** Hard stop on the page walk — a server that never stops handing out cursors is a bug, not
 *  an instruction to loop forever. Reaching it aborts the reconcile (nothing is evicted). */
export const MAX_PAGES = 200;
/** A row this device wrote less than this ago is never evicted (rule 4 above). */
export const RECENT_SAVE_GRACE_MS = 60_000;

/**
 * The minimal client surface (structural). Every member is OPTIONAL: the surfaces over this
 * seam are also mounted in tests and cut-down builds, and a client that cannot list is not
 * "offline" — it is a caller with no cloud, which takes the local-only path without claiming
 * an outage that did not happen.
 */
export interface ReconcileClient {
  listTemplatesPage?(query?: { limit?: number; cursor?: string }): Promise<TemplatePage>;
  /** `GET /api/templates/sync` — the only listing that can represent library / granted rows. */
  listSyncable?(): Promise<ProductTemplate[]>;
  getTemplate?(id: string): Promise<ProductTemplate>;
}

export interface ReconcileOptions {
  /** Engine binding, so an evicted template's local CASE IMAGE goes too. */
  engine?: Parameters<typeof deleteTemplateImage>[0];
  /** Templates the operator removed from THIS device (`deviceHidden.ts`). Never pulled back. */
  skipIds?: ReadonlySet<string>;
  /** D-4 (iii): an admin sees every status, a customer sees Active only — the same gate the
   *  Templates page applies in `rowSet.visibleRows`. Defaults to customer (false). */
  canSeeAllStatuses?: boolean;
  /** Clock seam for the grace window. */
  now?: () => number;
}

export interface ReconcileOutcome {
  /** The union to render: device records first, then the cloud-only rows. */
  templates: ProductTemplate[];
  /** Which of `templates` have a FULL local record (geometry present → instant selection). */
  onDeviceIds: Set<string>;
  /** Names evicted because the account can no longer reach them. */
  removed: string[];
  /** Cloud rows withheld because the operator removed them from this device. */
  hiddenSkipped: number;
  /** Per-row failures (`"<name>: <reason>"`) — never fatal, never silent. */
  failed: string[];
  /** The listing could not be read end to end; `templates` are the cached rows. */
  offline: boolean;
  /** The listing was EMPTY while the device holds rows — suspect, so nothing was removed. */
  emptySuspect: boolean;
  error: string | null;
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** A row `/api/templates` cannot represent by construction (rule 3). */
function beyondBrowseListing(t: ProductTemplate): boolean {
  return t.isLibrary === true || t.origin === "platform";
}

function isRowArray(rows: unknown): rows is ProductTemplate[] {
  return Array.isArray(rows)
    && rows.every((r) => !!r && typeof (r as ProductTemplate).templateId === "string"
      && (r as ProductTemplate).templateId !== "");
}

/**
 * Read the WHOLE browse listing, page by page. Throws on any failure — a partial listing is
 * not an authority, and the caller turns a throw into "offline, evict nothing".
 */
export async function readWholeListing(client: ReconcileClient): Promise<ProductTemplate[]> {
  const listPage = client.listTemplatesPage;
  if (typeof listPage !== "function") throw new Error("this client cannot page the template list");
  const rows: ProductTemplate[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const page = await listPage.call(client, { limit: PAGE_LIMIT, cursor });
    // SHAPE CHECK. `request()` answers an unparseable 200 with `undefined`, which the mapper
    // turns into an empty page — so "the server said nothing intelligible" arrives looking
    // exactly like "there is nothing". Refuse it here rather than treat it as an authority.
    if (!page || !isRowArray(page.items)) {
      throw new Error("the cloud template list came back in a shape this app cannot read");
    }
    rows.push(...page.items);
    const next = page.nextCursor;
    if (!next) return rows;
    if (seenCursors.has(next)) throw new Error("the cloud template list repeated a page cursor");
    seenCursors.add(next);
    cursor = next;
  }
  throw new Error(`the cloud template list did not end after ${MAX_PAGES} pages`);
}

/**
 * Whether the CLOUD summary says this device's record is out of date.
 *
 * The version-id comparison is guarded exactly as `autoSync.rehydrateChangedBaked` guards it
 * (`!!freshVer && freshVer !== localVer`): a never-activated Draft has `current_version_id:
 * null` on the summary, which `wireSummaryToTemplate` maps to `""`, while the FULL record
 * resolves the real id — so an unguarded `!==` reads EVERY such Draft as changed forever, and
 * costs a `getTemplate` per template per pass. The write token is compared only when both
 * sides carry one, for the same reason. `previewRef` is deliberately not compared: the detail
 * and summary resolve it from different places and a difference there is not a content change.
 *
 * (`updated_at` would be the better signal, but `TemplateSummary` does not carry it — see
 * `cloud/app/catalog/schemas.py`.)
 */
export function localRecordIsStale(local: ProductTemplate, cloud: ProductTemplate): boolean {
  const lv = local.currentVersion?.versionId ?? "";
  const cv = cloud.currentVersion?.versionId ?? "";
  if (cv && lv && cv !== lv) return true;
  const lt = local.currentVersion?.versionToken ?? "";
  const ct = cloud.currentVersion?.versionToken ?? "";
  if (lt && ct && lt !== ct) return true;
  if ((cloud.currentVersion?.status ?? "") !== (local.currentVersion?.status ?? "")) return true;
  if ((cloud.name ?? "") !== (local.name ?? "")) return true;
  return (cloud.code ?? "") !== (local.code ?? "");
}

/**
 * Download ONE template's full record and persist it on this device.
 *
 * This is the cost the Mock-ups page pays when the operator SELECTS a cloud-only row (or
 * presses Sync), and the only place it pays it. The page never fetches on behalf of a row the
 * operator has not asked for — that is what made "show the whole catalogue" affordable.
 */
export async function syncOneTemplate(
  io: ReconcileIO,
  client: ReconcileClient,
  templateId: string,
): Promise<ProductTemplate> {
  if (typeof client.getTemplate !== "function") {
    throw new Error("this client cannot fetch a template");
  }
  const full = await client.getTemplate(templateId);
  await saveSyncedTemplate(io, full);
  return full;
}

export async function reconcileSyncedTemplates(
  io: ReconcileIO,
  client: ReconcileClient,
  opts: ReconcileOptions = {},
): Promise<ReconcileOutcome> {
  const now = opts.now ?? Date.now;
  const skip = opts.skipIds ?? new Set<string>();
  const visible = (t: ProductTemplate) =>
    opts.canSeeAllStatuses === true || facetMatches("Active", headStatusOf(t));

  let localRows: SyncedTemplateRow[];
  try {
    localRows = await listSyncedTemplateRows(io);
  } catch {
    localRows = []; // no local store / unreadable → the cloud list is the whole answer
  }
  const localOnly = (over: Partial<ReconcileOutcome> = {}): ReconcileOutcome => ({
    templates: localRows.map((r) => r.template).filter(visible),
    onDeviceIds: new Set(localRows.map((r) => r.template.templateId)),
    removed: [], hiddenSkipped: 0, failed: [], offline: false, emptySuspect: false, error: null,
    ...over,
  });

  if (typeof client.listTemplatesPage !== "function") return localOnly();

  let cloud: ProductTemplate[];
  try {
    cloud = await readWholeListing(client);
  } catch (e) {
    return localOnly({ offline: true, error: message(e) });
  }

  // Rule 2 — an empty listing beside a non-empty device is suspect. Show the union (there is
  // nothing to union) and evict NOTHING.
  if (cloud.length === 0 && localRows.length > 0) return localOnly({ emptySuspect: true });

  const cloudIds = new Set(cloud.map((t) => t.templateId));

  // Rule 3 — a second authority, and only when a library/platform row is actually in question.
  let syncableIds: Set<string> | null = null;
  const needsSyncable = localRows.some(
    (r) => beyondBrowseListing(r.template) && !cloudIds.has(r.template.templateId));
  if (needsSyncable && typeof client.listSyncable === "function") {
    try {
      const rows = await client.listSyncable();
      // Same two guards as the browse listing: a shape it cannot read, and an empty answer
      // beside a non-empty device, are both "no authority" rather than "everything is gone".
      if (isRowArray(rows) && rows.length > 0) syncableIds = new Set(rows.map((r) => r.templateId));
    } catch {
      syncableIds = null; // unreachable → these rows are not judged at all
    }
  }

  const failed: string[] = [];
  const removed: string[] = [];
  const kept: ProductTemplate[] = [];

  for (const row of localRows) {
    const t = row.template;
    const gone = !cloudIds.has(t.templateId)
      // Rule 4 — written here moments ago; the listing predates it.
      && !(row.savedAt !== undefined && now() - row.savedAt < RECENT_SAVE_GRACE_MS)
      // Rule 3 — only judge a library/platform row against a listing that can represent it.
      && (beyondBrowseListing(t) ? syncableIds !== null && !syncableIds.has(t.templateId) : true);
    if (!gone) {
      kept.push(t);
      continue;
    }
    try {
      await removeSyncedTemplate(io, t.templateId, opts.engine);
      const versionId = t.currentVersion?.versionId;
      if (versionId) clearHydration(versionId);
      removed.push(t.name ?? t.templateId);
    } catch (e) {
      // A failed eviction keeps the row: it IS still on the device, and reporting a removal
      // that did not happen is how a stale list becomes an invisible one.
      kept.push(t);
      failed.push(`${t.name ?? t.templateId}: could not be removed from this device (${message(e)})`);
    }
  }

  // THE UNION. Device records win on identity — they carry the geometry the summary does not.
  const keptIds = new Set(kept.map((t) => t.templateId));
  const cloudOnly: ProductTemplate[] = [];
  let hiddenSkipped = 0;
  for (const r of cloud) {
    if (keptIds.has(r.templateId)) continue;
    if (skip.has(r.templateId)) { hiddenSkipped++; continue; }
    cloudOnly.push(r);
  }

  return {
    templates: [...kept, ...cloudOnly].filter(visible),
    onDeviceIds: keptIds,
    removed, hiddenSkipped, failed, offline: false, emptySuspect: false, error: null,
  };
}

/** The one-line result for the status area — `null` when there is nothing to report. */
export function reconcileMessage(r: ReconcileOutcome): string | null {
  if (r.offline) {
    return "Offline — showing the last synced list. Templates added or deleted in the cloud will appear once the connection is back.";
  }
  if (r.emptySuspect) {
    return "The cloud returned no templates — nothing was removed. Press Sync to try again.";
  }
  const parts: string[] = [];
  if (r.removed.length) {
    // NOT "deleted": a row can leave the listing because it was un-shared or because a role
    // changed, and the page cannot tell those from a delete.
    parts.push(
      `${r.removed.length} no longer available to this account and removed from this device (${r.removed.join(", ")})`,
    );
  }
  if (r.hiddenSkipped) {
    parts.push(
      `${r.hiddenSkipped} template${r.hiddenSkipped === 1 ? " is" : "s are"} hidden on this device — restore ${r.hiddenSkipped === 1 ? "it" : "them"} from Templates`,
    );
  }
  if (r.failed.length) parts.push(`${r.failed.length} failed — ${r.failed.join("; ")}`);
  return parts.length ? `Synced with the cloud — ${parts.join(", ")}.` : null;
}
